import React, { useState } from 'react';
import { Save, Loader2, CheckCircle, ArrowLeft, ArrowRight, Bell, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import WizardSidebar, { SidebarSection, QAItem } from './WizardSidebar';
import QAAssistantPanel from './QAAssistantPanel';
import CaseFilesModal from './CaseFilesModal';
import WizardViewModeRail from './WizardViewModeRail';

interface WizardV2LayoutProps {
  children: React.ReactNode;
  
  // Step info
  currentStep: number;
  highestCompletedStep?: number; // NEW: Track the furthest step the user has reached
  totalSteps?: number;
  stepTitle: string;
  stepSubtitle?: string;
  
  // Progress — optional; pass-through to WizardSidebar. When undefined the
  // progress UI hides entirely (no invented number). SSoT contract: backend
  // computes, frontend renders. See
  // docs/plans/2026-04-25-frontend-calc-drift-audit.md (Fix B).
  completionPercent?: number;
  grade?: 'A' | 'B' | 'C' | 'D' | 'F';
  
  // Sidebar content
  sections?: SidebarSection[];
  qaItems?: QAItem[];
  nextAction?: {
    label: string;
    description?: string;
    onClick?: () => void;
  };
  
  // Mode
  calculationMode?: 'standard_bi' | 'future_closure';
  
  // Save state
  isSaving?: boolean;
  lastSaved?: Date | null;
  onSave?: () => void;
  
  // Navigation
  onBack?: () => void;
  onNext?: () => void;
  goToStep?: (step: number) => void;
  nextLabel?: string;
  backLabel?: string;
  showBack?: boolean;
  showNext?: boolean;
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
  
  // Deep linking support
  onSectionClick?: (stepNumber: number, sectionId: string) => void;
  activeSection?: string;
  urlSection?: string | null;
  
  // Quick Actions support
  onQuickAction?: (action: string) => void;
  
  // QA Assistant support
  claimId?: string;
  anomalyCount?: number;
  reviewCount?: number;

  // Chromeless mode (DS Case Workspace acquisition, 2026-09-05). The host shell owns
  // page chrome + stage navigation, so this suppresses the WizardSidebar, the Bell and
  // the Step badge, and drops the h-screen/overflow framing so the layout sits inside a
  // stage panel. It KEEPS the save bar (the wizard's only forced-save path) and the
  // footer Back/Next (Steps 1–2 commit their local form into wizard data ONLY via
  // onNext; the host routes those through its clamped navigator). Layout/className only.
  chromeless?: boolean;
}

export default function WizardV2Layout({
  children,
  currentStep,
  highestCompletedStep,
  totalSteps = 7,
  stepTitle,
  stepSubtitle,
  completionPercent,
  grade,
  sections = [],
  qaItems = [],
  nextAction,
  calculationMode = 'standard_bi',
  isSaving = false,
  lastSaved,
  onSave,
  onBack,
  onNext,
  goToStep,
  nextLabel = 'Continue',
  backLabel = 'Back',
  showBack = true,
  showNext = true,
  isNextDisabled = false,
  isNextLoading = false,
  onSectionClick,
  activeSection,
  urlSection,
  onQuickAction,
  claimId,
  anomalyCount = 0,
  reviewCount = 0,
  chromeless = false,
}: WizardV2LayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [qaAssistantOpen, setQaAssistantOpen] = useState(false);
  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const navigate = useNavigate();

  // SSoT: Trust claimId prop from useClaimId hook
  const effectiveClaimId = claimId || '';

  // Handle quick actions - intercept 'files' action
  const handleQuickAction = (action: string) => {
    if (action === 'files') {
      setFilesModalOpen(true);
    } else if (onQuickAction) {
      onQuickAction(action);
    }
  };

  // Format last saved time
  const formatLastSaved = () => {
    if (!lastSaved) return '';
    const seconds = Math.floor((new Date().getTime() - lastSaved.getTime()) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <div
      className={chromeless ? 'flex w-full bg-[var(--color-bg-base)]' : 'h-screen flex bg-[var(--color-bg-base)]'}
      data-wizard-chromeless={chromeless ? 'true' : undefined}
    >
      {/* Sidebar — omitted in chromeless mode (the host shell owns stage navigation) */}
      {!chromeless && (
      <WizardSidebar
        currentStep={currentStep}
        highestCompletedStep={highestCompletedStep}
        totalSteps={totalSteps}
        completionPercent={completionPercent}
        grade={grade}
        sections={sections}
        qaItems={qaItems}
        nextAction={nextAction}
        calculationMode={calculationMode}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        goToStep={goToStep}
        onSectionClick={onSectionClick}
        activeSection={activeSection}
        urlSection={urlSection}
        onQuickAction={handleQuickAction}
        onOpenQAAssistant={() => setQaAssistantOpen(true)}
      />
      )}

      {/* Main content area */}
      <div className={chromeless ? 'flex-1 flex flex-col min-w-0 bg-[var(--color-bg-base)]' : 'flex-1 flex flex-col overflow-hidden bg-[var(--color-bg-base)]'}>
        {/* Top header bar - matching dashboard exactly */}
        <header className="h-16 bg-[var(--color-bg-surface)] border-b border-[var(--color-border)] flex items-center justify-between px-6 flex-shrink-0">
          {/* Left side - Title and subtitle like dashboard */}
          <div>
            <h1 className="cs-h2">{stepTitle}</h1>
            {stepSubtitle && (
              <p className="cs-meta">{stepSubtitle}</p>
            )}
          </div>

          {/* Right side - Actions like dashboard */}
          <div className="flex items-center gap-3">
            {/* Save status */}
            <div className="flex items-center gap-2 cs-meta">
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-[var(--color-brand)]" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 text-[var(--color-verified)]" />
                  <span>Saved{lastSaved ? ` ${formatLastSaved()}` : ''}</span>
                </>
              )}
            </div>

            {/* Divider */}
            <div className="h-8 w-px bg-[var(--color-border)]" />

            {/* Save button - styled like dashboard buttons */}
            {onSave && (
              <button
                onClick={onSave}
                disabled={isSaving}
                className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] rounded-lg transition-colors disabled:opacity-50"
                title="Save"
              >
                <Save className="w-5 h-5" />
              </button>
            )}

            {/* Notification bell — omitted in chromeless mode */}
            {!chromeless && (
            <button className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] rounded-lg transition-colors relative">
              <Bell className="w-5 h-5" />
            </button>
            )}

            {/* Step indicator badge - omitted in chromeless mode */}
            {!chromeless && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--color-bg-elevated)] rounded-lg">
              <span className="cs-label text-[var(--color-text-secondary)]">Step {currentStep}/{totalSteps}</span>
            </div>
            )}
          </div>
        </header>

        {/* Content area - FULL WIDTH to maximize screen real estate.
            Chromeless + sections present (step 3's ViewMode nav): render the K3 left rail
            beside the active view, so the acquired Upload / bank-CC / merchant / etc. views
            are reachable (chromeless suppressed the sidebar that used to carry this nav). */}
        <main className={chromeless ? 'flex-1 bg-[var(--color-bg-base)]' : 'flex-1 overflow-y-auto bg-[var(--color-bg-base)]'}>
          {chromeless && sections && sections.length > 0 ? (
            <div
              data-testid="ds-viewmode-layout"
              style={{
                display: 'grid',
                gridTemplateColumns: '224px minmax(0, 1fr)',
                columnGap: 24,
                paddingLeft: 24,
              }}
            >
              <WizardViewModeRail sections={sections} />
              <div style={{ minWidth: 0 }}>{children}</div>
            </div>
          ) : (
            <div className="h-full">
              {children}
            </div>
          )}
        </main>

        {/* Bottom navigation bar */}
        <footer className="h-16 bg-[var(--color-bg-surface)] border-t border-[var(--color-border)] flex items-center justify-between px-6 flex-shrink-0">
          <div>
            {showBack && onBack && (
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {backLabel}
              </button>
            )}
          </div>

          <div>
            {showNext && onNext && (
              <button
                onClick={onNext}
                disabled={isNextDisabled || isNextLoading}
                className={`
                  flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all
                  ${isNextDisabled || isNextLoading
                    ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-dim)] cursor-not-allowed'
                    : 'bg-[var(--color-brand)] text-[var(--color-on-brand)] hover:bg-[var(--color-brand-hover)] shadow-sm'
                  }
                `}
              >
                {isNextLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {nextLabel}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </footer>
      </div>
      
      {/* Case Files Modal */}
      <CaseFilesModal
        isOpen={filesModalOpen}
        onClose={() => setFilesModalOpen(false)}
        claimId={effectiveClaimId}
      />

      {/* QA Assistant Panel */}
      <QAAssistantPanel
        isOpen={qaAssistantOpen}
        onClose={() => setQaAssistantOpen(false)}
        claimId={effectiveClaimId}
        currentStep={currentStep}
        anomalyCount={anomalyCount}
        reviewCount={reviewCount}
        onNavigate={(target) => {
          // Handle navigation from QA Assistant
          if (onSectionClick && currentStep === 3) {
            onSectionClick(3, target);
          } else if (onQuickAction) {
            onQuickAction(target);
          }
          setQaAssistantOpen(false);
        }}
      />
    </div>
  );
}
